<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>Reports</h4>
          <h6>Choose a section, then the report you need</h6>
        </div>
      </div>
    </div>

    <div class="card report-hub border-0 shadow-sm">
      <!-- Section tabs -->
      <ul class="nav report-sections" role="tablist">
        <li v-for="section in visibleSections" :key="section.key" class="nav-item" role="presentation">
          <button
            type="button"
            class="nav-link"
            :class="{ active: section.key === activeSection?.key }"
            role="tab"
            :aria-selected="section.key === activeSection?.key"
            @click="selectSection(section.key)"
          >
            <span class="section-icon"><i class="ti" :class="section.icon"></i></span>
            <span class="section-text">
              <span class="section-label">{{ section.label }}</span>
              <small v-if="!section.hideTabs" class="section-count">
                {{ section.reports.length }} {{ section.reports.length === 1 ? 'report' : 'reports' }}
              </small>
            </span>
          </button>
        </li>
      </ul>

      <div v-if="activeSection" class="card-body report-body">
        <!-- Report tabs inside the section (hidden when the section is a single full-page report) -->
        <ul
          v-if="activeSection.reports.length && !activeSection.hideTabs"
          class="nav report-tabs mb-4"
          role="tablist"
        >
          <li v-for="report in activeSection.reports" :key="report.key" class="nav-item" role="presentation">
            <button
              type="button"
              class="nav-link"
              :class="{ active: report.key === activeReport?.key }"
              role="tab"
              :aria-selected="report.key === activeReport?.key"
              @click="selectReport(report.key)"
            >
              <i class="ti me-1" :class="report.icon || 'ti-file-analytics'"></i>{{ report.label }}
            </button>
          </li>
        </ul>

        <component :is="activeReport.component" v-if="activeReport && activeReport.component" />

        <div v-else class="report-empty">
          <span class="empty-icon">
            <i class="ti" :class="activeReport ? (activeReport.icon || 'ti-file-analytics') : activeSection.icon"></i>
          </span>
          <h5 class="mb-1">
            {{ activeReport ? activeReport.label : `No ${activeSection.label} reports yet` }}
          </h5>
          <p class="text-muted mb-0">
            {{ activeReport
              ? 'This report is being prepared and will be available here soon.'
              : 'Reports added to this section will appear here.' }}
          </p>
        </div>
      </div>

      <div v-else class="card-body">
        <div class="report-empty">
          <span class="empty-icon"><i class="ti ti-lock"></i></span>
          <h5 class="mb-1">No reports available</h5>
          <p class="text-muted mb-0">You don't have access to any report section.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { can, isAdminUser } from '@/helpers/permission';
import StockSummaryReport from './inventory/StockSummaryReport.vue';
import StockMovementReport from './inventory/StockMovementReport.vue';
import StockTransferReport from './inventory/StockTransferReport.vue';
import StockAdjustmentReport from './inventory/StockAdjustmentReport.vue';
import LowStockReport from './inventory/LowStockReport.vue';
import SlowMovingReport from './inventory/SlowMovingReport.vue';
import ExpiryReport from './inventory/ExpiryReport.vue';
import SalesRegisterReport from './sales/SalesRegisterReport.vue';
import SalesDetailsReport from './sales/SalesDetailsReport.vue';
import SalesReturnReport from './sales/SalesReturnReport.vue';
import SalesByProductReport from './sales/SalesByProductReport.vue';
import SalesByCategoryReport from './sales/SalesByCategoryReport.vue';
import SalesByCustomerReport from './sales/SalesByCustomerReport.vue';
import CustomerDueReport from './sales/CustomerDueReport.vue';
import PaymentMethodReport from './sales/PaymentMethodReport.vue';
import DiscountVatReport from './sales/DiscountVatReport.vue';
import SalesTrendReport from './sales/SalesTrendReport.vue';
import PurchaseRegisterReport from './purchase/PurchaseRegisterReport.vue';
import PurchaseDetailsReport from './purchase/PurchaseDetailsReport.vue';
import PurchaseReturnReport from './purchase/PurchaseReturnReport.vue';
import SupplierPaymentReport from './purchase/SupplierPaymentReport.vue';
import SupplierDueReport from './purchase/SupplierDueReport.vue';
import SupplierAgingReport from './purchase/SupplierAgingReport.vue';
import PurchaseByProductReport from './purchase/PurchaseByProductReport.vue';
import PurchaseCostReport from './purchase/PurchaseCostReport.vue';
import GeneralLedgerReport from './accounting/GeneralLedgerReport.vue';
import PartyLedgerReport from './accounting/PartyLedgerReport.vue';
import CashBookReport from './accounting/CashBookReport.vue';
import DayBookReport from './accounting/DayBookReport.vue';
import TrialBalanceReport from './accounting/TrialBalanceReport.vue';
import ProfitLossReport from './accounting/ProfitLossReport.vue';
import BalanceSheetReport from './accounting/BalanceSheetReport.vue';
import EquityChangesReport from './accounting/EquityChangesReport.vue';
import AuditTrailReport from './accounting/AuditTrailReport.vue';

/*
 * To add a report: put an entry in the section's `reports` list.
 *   { key: 'stock_summary', label: 'Stock Summary', icon: 'ti-packages', component: markRaw(StockSummaryReport) }
 * Without `component` the tab shows a "coming soon" panel; an optional `visible()` hides it from some users.
 */
const SECTIONS = [
  {
    key: 'inventory',
    label: 'Inventory',
    icon: 'ti-box',
    visible: () => can('inventory.report.stock', 'R') || can('inventory.report.low_stock', 'R'),
    reports: [
      {
        key: 'stock_summary',
        label: 'Stock Summary',
        icon: 'ti-packages',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(StockSummaryReport),
      },
      {
        key: 'stock_movement',
        label: 'Stock Movement',
        icon: 'ti-arrows-exchange',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(StockMovementReport),
      },
      {
        key: 'stock_transfer',
        label: 'Stock Transfer',
        icon: 'ti-truck',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(StockTransferReport),
      },
      {
        key: 'stock_adjustment',
        label: 'Adjustment',
        icon: 'ti-adjustments',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(StockAdjustmentReport),
      },
      {
        key: 'low_stock',
        label: 'Low Stock',
        icon: 'ti-alert-triangle',
        visible: () => can('inventory.report.low_stock', 'R'),
        component: markRaw(LowStockReport),
      },
      {
        key: 'slow_moving',
        label: 'Slow Moving',
        icon: 'ti-hourglass-low',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(SlowMovingReport),
      },
      {
        key: 'expiry',
        label: 'Expiry',
        icon: 'ti-calendar-time',
        visible: () => can('inventory.report.stock', 'R'),
        component: markRaw(ExpiryReport),
      },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: 'ti-shopping-cart',
    visible: () =>
      can('sales.report.sale', 'R') || can('sales.report.sale_return', 'R') || can('sales.report.customer_due', 'R'),
    reports: [
      {
        key: 'sales_register',
        label: 'Sale Register',
        icon: 'ti-file-invoice',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesRegisterReport),
      },
      {
        key: 'sales_details',
        label: 'Sales Details',
        icon: 'ti-list-details',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesDetailsReport),
      },
      {
        key: 'sales_return',
        label: 'Sales Return Register',
        icon: 'ti-receipt-refund',
        visible: () => can('sales.report.sale_return', 'R'),
        component: markRaw(SalesReturnReport),
      },
      {
        key: 'sales_by_product',
        label: 'Sales by Product',
        icon: 'ti-packages',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesByProductReport),
      },
      {
        key: 'sales_by_category',
        label: 'Sales by Category',
        icon: 'ti-category',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesByCategoryReport),
      },
      {
        key: 'sales_by_customer',
        label: 'Sales by Customer',
        icon: 'ti-users',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesByCustomerReport),
      },
      {
        key: 'customer_due',
        label: 'Customer Due',
        icon: 'ti-clock-dollar',
        visible: () => can('sales.report.customer_due', 'R'),
        component: markRaw(CustomerDueReport),
      },
      {
        key: 'payment_methods',
        label: 'Payment Methods',
        icon: 'ti-credit-card',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(PaymentMethodReport),
      },
      {
        key: 'discount_vat',
        label: 'Discount & VAT',
        icon: 'ti-receipt-tax',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(DiscountVatReport),
      },
      {
        key: 'sales_trend',
        label: 'Hourly / Daily Sales',
        icon: 'ti-chart-line',
        visible: () => can('sales.report.sale', 'R'),
        component: markRaw(SalesTrendReport),
      },
    ],
  },
  {
    key: 'purchase',
    label: 'Purchase',
    icon: 'ti-truck-delivery',
    visible: () =>
      can('purchase.report.buy', 'R') || can('purchase.report.batch', 'R') || can('purchase.report.supplier_payment', 'R'),
    reports: [
      {
        key: 'purchase_register',
        label: 'Purchase Register',
        icon: 'ti-file-invoice',
        visible: () => can('purchase.report.buy', 'R'),
        component: markRaw(PurchaseRegisterReport),
      },
      {
        key: 'purchase_details',
        label: 'Purchase Details',
        icon: 'ti-list-details',
        visible: () => can('purchase.report.buy', 'R'),
        component: markRaw(PurchaseDetailsReport),
      },
      {
        key: 'purchase_return',
        label: 'Purchase Return Register',
        icon: 'ti-truck-return',
        visible: () => can('purchase.report.buy', 'R'),
        component: markRaw(PurchaseReturnReport),
      },
      {
        key: 'supplier_payments',
        label: 'Supplier Payments',
        icon: 'ti-cash',
        visible: () => can('purchase.report.supplier_payment', 'R'),
        component: markRaw(SupplierPaymentReport),
      },
      {
        key: 'supplier_due',
        label: 'Supplier Due',
        icon: 'ti-clock-dollar',
        visible: () => can('purchase.report.supplier_payment', 'R'),
        component: markRaw(SupplierDueReport),
      },
      {
        key: 'supplier_aging',
        label: 'Supplier Aging',
        icon: 'ti-hourglass',
        visible: () => can('purchase.report.supplier_payment', 'R'),
        component: markRaw(SupplierAgingReport),
      },
      {
        key: 'purchase_by_product',
        label: 'Purchase by Product',
        icon: 'ti-package',
        visible: () => can('purchase.report.buy', 'R'),
        component: markRaw(PurchaseByProductReport),
      },
      {
        key: 'purchase_cost',
        label: 'Cost Comparison',
        icon: 'ti-scale',
        visible: () => can('purchase.report.batch', 'R'),
        component: markRaw(PurchaseCostReport),
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Accounts',
    icon: 'ti-currency-dollar',
    visible: () => isAdminUser(),
    reports: [
      { key: 'general_ledger', label: 'General Ledger', icon: 'ti-book', component: markRaw(GeneralLedgerReport) },
      { key: 'party_ledger', label: 'Party Ledger', icon: 'ti-users', component: markRaw(PartyLedgerReport) },
      { key: 'cash_book', label: 'Cash & Bank Book', icon: 'ti-wallet', component: markRaw(CashBookReport) },
      { key: 'day_book', label: 'Day Book', icon: 'ti-notebook', component: markRaw(DayBookReport) },
      { key: 'trial_balance', label: 'Trial Balance', icon: 'ti-scale', component: markRaw(TrialBalanceReport) },
      { key: 'profit_loss', label: 'Profit & Loss', icon: 'ti-report-money', component: markRaw(ProfitLossReport) },
      { key: 'balance_sheet', label: 'Balance Sheet', icon: 'ti-building-bank', component: markRaw(BalanceSheetReport) },
      { key: 'equity_changes', label: 'Changes in Equity', icon: 'ti-user-dollar', component: markRaw(EquityChangesReport) },
    ],
  },
  {
    key: 'audit',
    label: 'Audit Trail',
    icon: 'ti-history',
    hideTabs: true,
    visible: () => isAdminUser(),
    reports: [
      {
        key: 'audit_trail',
        label: 'Audit Trail',
        icon: 'ti-history',
        component: markRaw(AuditTrailReport),
      },
    ],
  },
];

export default {
  name: 'Reports',

  computed: {
    visibleSections() {
      return SECTIONS
        .filter((section) => section.visible())
        .map((section) => ({
          ...section,
          reports: section.reports.filter((report) => !report.visible || report.visible()),
        }));
    },

    // The selection lives in the URL (?section=…&report=…) so refresh and back/forward keep it.
    activeSection() {
      const sections = this.visibleSections;
      return sections.find((section) => section.key === this.$route.query.section) || sections[0] || null;
    },

    activeReport() {
      const reports = this.activeSection?.reports || [];
      return reports.find((report) => report.key === this.$route.query.report) || reports[0] || null;
    },
  },

  methods: {
    selectSection(key) {
      if (key === this.activeSection?.key) return;
      this.$router.replace({ query: { section: key } });
    },

    selectReport(key) {
      if (key === this.activeReport?.key) return;
      this.$router.replace({ query: { section: this.activeSection.key, report: key } });
    },
  },
};
</script>

<style scoped>
.report-hub {
  border-radius: 10px;
  overflow: hidden;
}

/* Section tabs: large, icon + label, underline on the active one */
.report-sections {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 1rem;
  border-bottom: 1px solid #E6EAED;
  background: #FAFBFE;
}

.report-sections .nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: 0;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  background: transparent;
  color: #646B72;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.report-sections .nav-link:hover {
  color: #FE9F43;
  background: rgba(254, 159, 67, 0.04);
}

.report-sections .nav-link.active {
  color: #092C4C;
  border-bottom-color: #FE9F43;
  background: #ffffff;
}

.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #F2F4F7;
  color: #646B72;
  font-size: 1.15rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.report-sections .nav-link.active .section-icon,
.report-sections .nav-link:hover .section-icon {
  background: rgba(254, 159, 67, 0.12);
  color: #FE9F43;
}

.section-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.section-label {
  font-weight: 600;
  font-size: 0.95rem;
}

.section-count {
  color: #9AA1A9;
  font-size: 0.75rem;
}

/* Report tabs inside a section: compact pills */
.report-body {
  padding: 1.5rem;
}

.report-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px dashed #E6EAED;
}

.report-tabs .nav-link {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 1rem;
  border: 1px solid #E6EAED;
  border-radius: 999px;
  background: #ffffff;
  color: #092C4C;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.15s ease;
}

.report-tabs .nav-link:hover {
  border-color: #FE9F43;
  color: #FE9F43;
}

.report-tabs .nav-link.active {
  border-color: #FE9F43;
  background: #FE9F43;
  color: #ffffff;
}

/* Placeholder until a report is plugged in */
.report-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 2rem 1rem;
  border: 1px dashed #E6EAED;
  border-radius: 10px;
  background: #FCFCFD;
  text-align: center;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 1rem;
  border-radius: 50%;
  background: rgba(254, 159, 67, 0.12);
  color: #FE9F43;
  font-size: 1.75rem;
}

@media (max-width: 575.98px) {
  .report-sections {
    padding: 0;
  }

  .report-sections .nav-link {
    padding: 0.75rem;
  }

  .section-count {
    display: none;
  }
}
</style>
